import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';

import { PERMISSIONS } from '@/features/auth/permissions';
import { AppLayout } from '@/layouts/AppLayout/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout/AuthLayout';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage';

import { ROUTES } from './paths';
import { PublicOnlyRoute } from './PublicOnlyRoute';
import { RequireAuth } from './RequireAuth';
import {
  AccountPage,
  ApiClientsPage,
  DashboardPage,
  DocumentReviewPage,
  DocumentsPage,
  DocumentUploadPage,
  LoginPage,
  RolesPage,
  UsersPage,
} from './lazyPages';
import { RequirePermission } from './RequirePermission';
import { RouteErrorPage } from './RouteErrorPage';

/**
 * Route tree:
 * - public-only routes (login) inside AuthLayout;
 * - protected routes (RequireAuth) inside AppLayout, with permission-guarded admin routes;
 * - catch-all Not Found.
 */
export const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <PublicOnlyRoute />,
        children: [
          {
            element: <AuthLayout />,
            children: [{ path: ROUTES.login, element: <LoginPage /> }],
          },
        ],
      },
      {
        element: <RequireAuth />,
        children: [
          {
            path: ROUTES.root,
            element: <AppLayout />,
            children: [
              { index: true, element: <Navigate to={ROUTES.documents} replace /> },
              { path: 'dashboard', element: <DashboardPage /> },
              {
                path: 'documents',
                children: [
                  {
                    index: true,
                    element: (
                      <RequirePermission permission={PERMISSIONS.DOCUMENTS_READ}>
                        <DocumentsPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'upload',
                    element: (
                      <RequirePermission permission={PERMISSIONS.DOCUMENTS_UPLOAD}>
                        <DocumentUploadPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: ':documentId',
                    element: (
                      <RequirePermission permission={PERMISSIONS.DOCUMENTS_READ}>
                        <DocumentReviewPage />
                      </RequirePermission>
                    ),
                  },
                ],
              },
              { path: 'account', element: <AccountPage /> },
              {
                path: 'unauthorized',
                element: <UnauthorizedPage />,
              },
              {
                path: 'admin',
                children: [
                  { index: true, element: <Navigate to={ROUTES.users} replace /> },
                  {
                    path: 'users',
                    element: (
                      <RequirePermission permission={PERMISSIONS.USERS_READ}>
                        <UsersPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'roles',
                    element: (
                      <RequirePermission permission={PERMISSIONS.ROLES_READ}>
                        <RolesPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'api-keys',
                    element: (
                      <RequirePermission permission={PERMISSIONS.API_CLIENTS_MANAGE}>
                        <ApiClientsPage />
                      </RequirePermission>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        element: <AuthLayout />,
        children: [{ path: '*', element: <NotFoundPage /> }],
      },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
